import React, { useEffect, useId, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronIcon } from './Icons';
import { brandLink, categoryLink } from './retailUtils';

const OPEN_DELAY_MS = 120;  // hover intent: don't flash open while the pointer just passes over
const CLOSE_DELAY_MS = 200; // grace period to move the pointer from the trigger into the panel

/** A desktop header entry with a mega-menu panel — "Categories" and "Brands". Same behaviour as
 *  the retail store's: opens on hover (with intent delay) or on the ▾ button for keyboard and
 *  touch users; closes on Esc, pointer leave, focus leave and navigation. The label itself is a
 *  link (to), so a click still goes somewhere useful. */
export default function MegaMenu({ label, to, active, panelLabel, foot, children }) {
  const [open, setOpen] = useState(false);
  const timer = useRef();
  const toggleRef = useRef();
  const panelId = useId();
  const location = useLocation();

  useEffect(() => { setOpen(false); }, [location.pathname, location.search]);
  useEffect(() => () => clearTimeout(timer.current), []);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = e => { if (e.key === 'Escape') { setOpen(false); toggleRef.current?.focus(); } };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const setLater = (value, ms) => { clearTimeout(timer.current); timer.current = setTimeout(() => setOpen(value), ms); };
  const onBlur = e => { if (!e.currentTarget.contains(e.relatedTarget)) setOpen(false); };

  return (
    <div className="rt-mega" onMouseEnter={() => setLater(true, OPEN_DELAY_MS)} onMouseLeave={() => setLater(false, CLOSE_DELAY_MS)} onBlur={onBlur}>
      <Link to={to} className={active ? 'active' : undefined} aria-current={active ? 'page' : undefined}>{label}</Link>
      {children && (
        <button ref={toggleRef} type="button" className="rt-mega-toggle" aria-expanded={open} aria-controls={panelId}
          aria-label={panelLabel} onClick={() => { clearTimeout(timer.current); setOpen(o => !o); }}>
          <ChevronIcon size={12} />
        </button>
      )}
      {open && children && (
        <div id={panelId} className="rt-mega-panel" role="region" aria-label={panelLabel}>
          <div className="rt-mega-inner">{children}</div>
          {foot && <div className="rt-mega-foot">{foot}</div>}
        </div>
      )}
    </div>
  );
}

/** Every category and its types (Myntra's department → type), biggest categories first.
 *  categories: from catalogApi.loadCategories. */
export function CategoryColumns({ categories }) {
  return categories.map(c => (
    <div key={c.name} className="rt-mega-col">
      <h3><Link to={categoryLink(c.name)}>{c.name}{c.count != null && <span className="rt-mega-count">{c.count}</span>}</Link></h3>
      <ul>
        {c.subcategories.map(s => (
          <li key={s}><Link to={categoryLink(c.name, s)}>{s}</Link></li>
        ))}
      </ul>
    </div>
  ));
}

/** Brands as logo tiles with their name (some logos are too small to read on their own).
 *  brands: catalogApi.useTopBrands, biggest first. */
export function BrandTiles({ brands, compact = false }) {
  return (
    <div className={`sf-brand-tiles${compact ? ' sf-brand-tiles--compact' : ''}`}>
      {brands.map(b => (
        <Link key={b.brandName} to={brandLink(b.brandName)} className="sf-brand-tile">
          {b.logoUrl && <img src={b.logoUrl} alt="" loading="lazy" decoding="async" />}
          <span className="sf-brand-tile-name">{b.brandName}</span>
          {!compact && <span className="sf-brand-tile-count">{b.productCount} products</span>}
        </Link>
      ))}
    </div>
  );
}

/** Every brand A–Z as a compact text list in columns — the header's Brands menu. */
export function BrandColumns({ brands }) {
  const sorted = [...brands].sort((a, b) => a.brandName.localeCompare(b.brandName, 'en', { sensitivity: 'base' }));
  return (
    <ul className="sf-brand-cols">
      {sorted.map(b => (
        <li key={b.brandName}><Link to={brandLink(b.brandName)}>{b.brandName}</Link></li>
      ))}
    </ul>
  );
}
